/**
 * Configuration & administration schemas (screen 23, M10).
 *
 * The SINGLE source of validation for the tenant configuration entities, shared by
 * the backoffice and the edge functions:
 *  - tenant_settings   — 1 row/tenant: numbering formats, thresholds, quiet hours,
 *                        operator session length.
 *  - shifts            — named work shifts (start/end/sort), soft-deletable.
 *  - warehouse_zones   — Zones of the obrador's physical structure, soft-deletable.
 *  - sections          — Seccions: optional subdivision of a Zona (also the OT
 *                        assignment target planta reads), soft-deletable.
 *  - storage_locations — Ubicacions: the final physical stock point, inside a Zona
 *                        or a Secció that allows them, soft-deletable.
 *  - devices           — registered hardware (printers/scales/…), soft-deletable.
 *  - user_roles        — role assignment per tenant member.
 *
 * KEY INVARIANT — multitenancy: `tenant_id` is NEVER accepted from client input (RLS +
 * the DB DEFAULT inject it). The *Input schemas omit it; `.strict()` rejects any attempt
 * to set it. Soft-delete goes through the shared SoftDeleteInput (mandatory reason).
 */

import { z } from 'zod';
import {
  AREA_FUNCTIONS,
  AREA_STOCK_MODES,
  STORAGE_CONDITIONS,
  DEVICE_TYPES,
  DEVICE_STATUSES,
  USER_ROLES,
  UNIT_DIMENSIONS,
  ARTICLE_TYPES,
  SKU_NUMBERING_MODES,
  TECH_SHEET_DESIGNS,
  INGREDIENTS_COMPOSITION_RULES,
  LABEL_TYPES,
  RECIPE_STEP_CONTROL_TYPES,
} from '../enums/index.js';
import {
  uuid,
  nonEmptyString,
  money,
  percent,
  quantity,
  timestampFields,
  softDeleteFields,
} from './common.js';
import {
  RECEPTION_TEMPERATURE_MAX_C,
  RECEPTION_TEMPERATURE_MIN_C,
} from '../domain/receptionQuality.js';
import { LabelLayout } from './label.js';
import { LabelPrinterConfig } from './label-printer.js';

/**
 * A clock time of day (HH:MM or HH:MM:SS), the read/write shape for a Postgres `time`
 * column (quiet hours / shift start+end). PostgREST returns `time` as a string.
 */
export const timeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'expected HH:MM');

// ── tenant_settings ───────────────────────────────────────────────────────────────

/**
 * The editable tenant configuration. A numbering format must contain the `{seq…}`
 * placeholder (the sequence is server-rendered; lot/OT numbers are never typed
 * manually — CLAUDE.md). Thresholds are percentages [0, 100]; lead days / session
 * hours are positive integers. Quiet hours are optional (both null = no quiet window).
 */
export const numberingFormat = nonEmptyString.regex(
  /\{seq(:0\d+)?\}/,
  'must contain a {seq} placeholder',
);

const pct = z.number().finite().min(0).max(100);

/**
 * Per-tipologia SKU masks (sku_numbering_formats_json), used when
 * sku_numbering_mode = 'per_type'. Every tipologia carries its own {seq} mask.
 */
export const SkuNumberingFormats = z
  .object({
    raw_material: numberingFormat,
    intermediate: numberingFormat,
    finished: numberingFormat,
    packaging: numberingFormat,
  })
  .strict();
export type SkuNumberingFormats = z.infer<typeof SkuNumberingFormats>;

export const TenantSettingsUpdateInput = z
  .object({
    lot_numbering_format: numberingFormat,
    work_order_numbering_format: numberingFormat,
    purchase_order_numbering_format: numberingFormat,
    reception_numbering_format: numberingFormat,
    sales_order_numbering_format: numberingFormat,
    shipment_numbering_format: numberingFormat,
    purchase_return_numbering_format: numberingFormat,
    sku_numbering_mode: z.enum(SKU_NUMBERING_MODES),
    sku_numbering_format: numberingFormat,
    sku_numbering_formats_json: SkuNumberingFormats,
    /** Màscara del codi de proveïdor (mestre · proveïdors); per defecte PRV-{seq:04}. */
    supplier_code_format: numberingFormat,
    /** Màscara del codi de client (mestre · clients); per defecte CLI-{seq:04}. */
    client_code_format: numberingFormat,
    /** Màscara del codi de zona (mestre · zones); per defecte ZON-{seq:03}. */
    zone_code_format: numberingFormat,
    sales_barcode_enabled: z.boolean(),
    /**
     * Com es construeix la declaració d'ingredients de la FT i de l'etiqueta.
     * Un sol valor per obrador: totes dues surten de la mateixa cadena, i dos
     * productes iguals no poden acabar declarats diferent.
     */
    ingredients_composition_rule: z.enum(INGREDIENTS_COMPOSITION_RULES),
    default_merma_threshold_pct: pct,
    weighing_deviation_threshold_pct: pct,
    expiry_alert_lead_days: z.number().int().min(0).max(365),
    operator_session_hours: z.number().int().min(1).max(24),
    /** Minuts sense tocar planta que la bloquegen i en demanen el PIN; 0 = no es bloqueja per inactivitat. */
    operator_idle_minutes: z.number().int().min(0).max(240),
    quiet_hours_start: timeOfDay.nullable(),
    quiet_hours_end: timeOfDay.nullable(),
  })
  .strict();
export type TenantSettingsUpdateInput = z.infer<typeof TenantSettingsUpdateInput>;

/**
 * The FT header / branding block (tenant_settings.ft_branding_json), rendered
 * at the top of the Fitxa Tècnica PDF. Every field is optional — the obrador
 * fills what it has; legal_name / tax_id are seeded from the tenant identity.
 * Kept to a known shape so the PDF renderer reads it without guessing keys.
 */
export const FtBranding = z
  .object({
    legal_name: nonEmptyString.optional(),
    tax_id: nonEmptyString.optional(),
    address: nonEmptyString.optional(),
    city: nonEmptyString.optional(),
    postal_code: nonEmptyString.optional(),
    province: nonEmptyString.optional(),
    phone: nonEmptyString.optional(),
    email: z.string().trim().email().optional(),
    website: nonEmptyString.optional(),
    /** Sanitary registry number (RGSEAA / RSIPAC), shown as "RMEA" on the FT. */
    sanitary_registry: nonEmptyString.optional(),
  })
  .strict();
export type FtBranding = z.infer<typeof FtBranding>;

/**
 * Branding update payload (settings · Marca i FT). Independent of the wider
 * TenantSettingsUpdateInput so the branding tab writes ONLY these columns.
 * tenant_id is never accepted from the client (RLS + DB DEFAULT inject it).
 * The logo url/key are nullable so clearing the logo persists the clear.
 */
export const TenantBrandingUpdateInput = z
  .object({
    ft_logo_url: nonEmptyString.nullable(),
    ft_logo_key: nonEmptyString.nullable(),
    ft_branding_json: FtBranding,
    tech_sheet_design: z.enum(TECH_SHEET_DESIGNS),
  })
  .strict();
export type TenantBrandingUpdateInput = z.infer<typeof TenantBrandingUpdateInput>;

/** Persisted tenant_settings row (read shape). 1:1 with the tenant. */
export const TenantSettings = z
  .object({
    id: uuid,
    tenant_id: uuid,
    lot_numbering_format: z.string(),
    work_order_numbering_format: z.string(),
    purchase_order_numbering_format: z.string(),
    reception_numbering_format: z.string(),
    sales_order_numbering_format: z.string(),
    shipment_numbering_format: z.string(),
    purchase_return_numbering_format: z.string(),
    sku_numbering_mode: z.enum(SKU_NUMBERING_MODES),
    sku_numbering_format: z.string(),
    sku_numbering_formats_json: z.record(z.string(), z.unknown()),
    supplier_code_format: z.string(),
    client_code_format: z.string(),
    zone_code_format: z.string(),
    sales_barcode_enabled: z.boolean(),
    ingredients_composition_rule: z.enum(INGREDIENTS_COMPOSITION_RULES),
    default_merma_threshold_pct: z.number(),
    weighing_deviation_threshold_pct: z.number(),
    expiry_alert_lead_days: z.number().int(),
    quiet_hours_start: z.string().nullable(),
    quiet_hours_end: z.string().nullable(),
    operator_session_hours: z.number().int(),
    operator_idle_minutes: z.number().int(),
    ft_logo_url: z.string().nullable(),
    ft_logo_key: z.string().nullable(),
    ft_branding_json: z.record(z.string(), z.unknown()),
    tech_sheet_design: z.enum(TECH_SHEET_DESIGNS),
    settings_json: z.record(z.string(), z.unknown()),
    ...timestampFields,
  })
  .strict();
export type TenantSettings = z.infer<typeof TenantSettings>;

// ── shifts ──────────────────────────────────────────────────────────────────────

export const ShiftInput = z
  .object({
    name: nonEmptyString,
    start_time: timeOfDay.nullable(),
    end_time: timeOfDay.nullable(),
    sort_order: z.number().int().min(0).default(0),
  })
  .strict();
export type ShiftInput = z.infer<typeof ShiftInput>;

export const Shift = z
  .object({
    id: uuid,
    tenant_id: uuid,
    name: z.string(),
    start_time: z.string().nullable(),
    end_time: z.string().nullable(),
    sort_order: z.number().int(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Shift = z.infer<typeof Shift>;

// ── Estructura de l'obrador: Zona → Secció → Ubicació ─────────────────────────────
//
// A Zona and a Secció carry the same logistic attributes: «Magatzem» is not a level
// of the hierarchy but the FUNCTION of an area, and how stock behaves there is its
// own choice (a warehouse zone may hold a refrigerated section with locations).

/** The logistic attributes shared by a Zona and a Secció. */
const areaAttributes = {
  /** Tipus funcional: Magatzem, Producció, Recepció, Expedició, Qualitat, Altres. */
  function_type: z.enum(AREA_FUNCTIONS),
  /** Comportament logístic: Estoc, Aprovisionament, Sense existències. */
  stock_mode: z.enum(AREA_STOCK_MODES),
  /** Whether Ubicacions may be created inside (stock precision is configurable). */
  allows_locations: z.boolean(),
  /** Conservació (optional): Ambient, Refrigerat, Congelat. */
  storage_condition: z.enum(STORAGE_CONDITIONS).nullable(),
};

// ── warehouse_zones (Zona) ──────────────────────────────────────────────────────

export const WarehouseZoneInput = z
  .object({
    /**
     * Codi de la zona, únic per obrador entre les actives. Opcional a propòsit:
     * el mestre el pot rebre, però mai és obligatori — quan falta, el trigger
     * `assign_zone_code` el genera amb la màscara de l'obrador. És també la clau
     * per la qual l'importador d'articles hi lliga la zona per defecte, així que
     * enviar-lo buit NO és el mateix que no enviar-lo: `nonEmptyString` rebutja
     * la cadena buida perquè un codi en blanc no hi arribi disfressat de valor.
     */
    code: nonEmptyString.nullable().optional(),
    name: nonEmptyString,
    ...areaAttributes,
  })
  .strict();
export type WarehouseZoneInput = z.infer<typeof WarehouseZoneInput>;

export const WarehouseZone = z
  .object({
    id: uuid,
    code: z.string().trim().min(1).nullable(),
    tenant_id: uuid,
    name: z.string(),
    ...areaAttributes,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type WarehouseZone = z.infer<typeof WarehouseZone>;

// ── sections (Secció) ───────────────────────────────────────────────────────────

/**
 * A Secció always hangs from a Zona when it is written: the sections that predate
 * the structure have no zone (the DB column is nullable for them), and saving one
 * is exactly the moment to give it one. `code` is optional and never generated.
 */
export const SectionInput = z
  .object({
    zone_id: uuid,
    code: nonEmptyString.nullable().optional(),
    name: nonEmptyString,
    description: nonEmptyString.nullable().optional(),
    ...areaAttributes,
  })
  .strict();
export type SectionInput = z.infer<typeof SectionInput>;

export const Section = z
  .object({
    id: uuid,
    tenant_id: uuid,
    zone_id: uuid.nullable(),
    code: z.string().trim().min(1).nullable(),
    name: z.string(),
    description: z.string().nullable(),
    ...areaAttributes,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Section = z.infer<typeof Section>;

// ── storage_locations (Ubicació) ────────────────────────────────────────────────

/**
 * The final physical point where stock can sit. No mandatory aisle / rack / level
 * levels: that detail lives in the code (`C-02-03-02-01`), unique per obrador. The
 * parent (zone, or section — whose zone the server derives) is fixed at creation:
 * an update may only change `code` and `description` (StorageLocationUpdateInput).
 */
export const StorageLocationInput = z
  .object({
    zone_id: uuid,
    section_id: uuid.nullable(),
    code: nonEmptyString,
    description: nonEmptyString.nullable().optional(),
  })
  .strict();
export type StorageLocationInput = z.infer<typeof StorageLocationInput>;

export const StorageLocationUpdateInput = StorageLocationInput.pick({
  code: true,
  description: true,
}).strict();
export type StorageLocationUpdateInput = z.infer<typeof StorageLocationUpdateInput>;

export const StorageLocation = z
  .object({
    id: uuid,
    tenant_id: uuid,
    zone_id: uuid,
    section_id: uuid.nullable(),
    code: z.string(),
    description: z.string().nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type StorageLocation = z.infer<typeof StorageLocation>;

// ── units ──────────────────────────────────────────────────────────────────────

/**
 * A unit of measure (units master). Per-tenant, editable from the Settings · Unitats
 * tab. `code` is the short symbol shown next to quantities across the apps (kg · L ·
 * Un) and is unique per tenant among the active units; `name` is the full label;
 * `dimension` ties it to mass / volume / count / length; `is_base` marks the canonical
 * unit of its dimension. tenant_id is NEVER accepted from the client (RLS + the DB
 * DEFAULT inject it) and `.strict()` rejects a forged one.
 */
export const UnitInput = z
  .object({
    code: nonEmptyString,
    name: nonEmptyString,
    dimension: z.enum(UNIT_DIMENSIONS),
    is_base: z.boolean().default(false),
  })
  .strict();
export type UnitInput = z.infer<typeof UnitInput>;

// ── taxes ──────────────────────────────────────────────────────────────────────

/**
 * An applicable tax (taxes master). Per-tenant, editable from Settings · Impostos
 * and admin-only to write (fiscal data, same gate as units).
 *
 * The article card PICKS one instead of holding a hand-typed percentage: the rate
 * then lives in ONE place, can be corrected once for every article that uses it,
 * and arrives at invoicing (Onada 2) as something with a name rather than a loose
 * number. `is_default` marks the one offered on a new article — at most one per
 * obrador, enforced by a partial unique index.
 *
 * tenant_id is NEVER accepted from the client (RLS + the DB DEFAULT inject it)
 * and `.strict()` rejects a forged one.
 */
export const TaxInput = z
  .object({
    name: nonEmptyString,
    rate_pct: percent,
    is_default: z.boolean().default(false),
  })
  .strict();
export type TaxInput = z.infer<typeof TaxInput>;

// ── packaging_units ─────────────────────────────────────────────────────────────

/**
 * A packaging / logistic unit (packaging_units master). Per-tenant, editable from the
 * Settings · Unitats d'embalatge tab: the physical packaging an obrador ships or
 * stores in (Sac, Caixa, Palet…). A simple named master — `name` is unique per tenant
 * among the active rows. tenant_id is NEVER accepted from the client (RLS + the DB
 * DEFAULT inject it) and `.strict()` rejects a forged one.
 */
export const PackagingUnitInput = z
  .object({
    name: nonEmptyString,
  })
  .strict();
export type PackagingUnitInput = z.infer<typeof PackagingUnitInput>;

// ── conservation / usage / preparation / container / compliance / ingredient masters ──

/**
 * The catalogue's configurable named masters (screen 23), all sharing the
 * packaging_units shape: a tenant-scoped `name`, unique among active rows,
 * soft-deletable, seeded with sensible defaults and extendable per obrador.
 *  - conservation_conditions — Ambient, Refrigerat (0-4 ºC)…
 *  - usage_types             — Consum directe, Ús en elaboració…
 *  - preparation_modes       — Llest per consumir, Coure abans…
 *  - container_types         — the SALES envàs (Pot, Ampolla…), distinct from
 *                              the logistic packaging units
 *  - compliance_items        — legislació i compliment (No conté OMG…)
 *  - ingredients             — declared-composition master (creatable on the fly)
 */
const namedMasterInput = z.object({ name: nonEmptyString }).strict();

/**
 * A temperature of the conservation master (°C): finite, inside the range worth writing down at reception, and
 * `null` to clear it (OBR-167). Two decimals, like the column (`numeric(5,2)`) — anything finer is rounded there.
 */
const conservationTemperature = z
  .number()
  .finite()
  .min(RECEPTION_TEMPERATURE_MIN_C)
  .max(RECEPTION_TEMPERATURE_MAX_C);

/**
 * Condicions de conservació (OBR-167): a més del nom, el rang de temperatura acceptable en rebre un article
 * d'aquesta condició i si cal anotar-la. Cada camp és OPCIONAL a l'escriptura —un `null` esborra el valor, i
 * un camp absent el deixa com està— perquè un canvi de nom no toqui el rang. El mínim no pot passar del màxim
 * (la mateixa regla que el CHECK de la base de dades). No es fa cap còpia a l'article: hi apunta per
 * `conservation_condition_id` (decisió 3 del pla de Qualitat).
 */
export const ConservationConditionInput = z
  .object({
    name: nonEmptyString,
    temp_min_c: conservationTemperature.nullable().optional(),
    temp_max_c: conservationTemperature.nullable().optional(),
    requires_reception_temperature: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.temp_min_c === null ||
      v.temp_min_c === undefined ||
      v.temp_max_c === null ||
      v.temp_max_c === undefined ||
      v.temp_min_c <= v.temp_max_c,
    { message: 'temp_min_c must not be greater than temp_max_c', path: ['temp_min_c'] },
  );
export type ConservationConditionInput = z.infer<typeof ConservationConditionInput>;
export const UsageTypeInput = namedMasterInput;
export type UsageTypeInput = z.infer<typeof UsageTypeInput>;
export const PreparationModeInput = namedMasterInput;
export type PreparationModeInput = z.infer<typeof PreparationModeInput>;
export const ContainerTypeInput = namedMasterInput;
export type ContainerTypeInput = z.infer<typeof ContainerTypeInput>;
export const ComplianceItemInput = namedMasterInput;
export type ComplianceItemInput = z.infer<typeof ComplianceItemInput>;
export const IngredientInput = namedMasterInput;
export type IngredientInput = z.infer<typeof IngredientInput>;

// ── processes ────────────────────────────────────────────────────────────────────

/**
 * A Procés (processes master, settings · Configuració · Processos, right after
 * Ingredients): una capacitat reutilitzable de l'obrador (Amassat, Cocció…) que fa
 * també de PLANTILLA per crear passos de recepta (OBR-17). Quan algú afegeix un pas
 * «des d'un procés», la recepta en copia la durada, la temperatura, el control, les
 * instruccions de base i els recursos per defecte com a valors inicials —que després
 * s'adapten a la recepta—, així no s'ha d'escriure tot de nou cada cop (CLAUDE.md
 * «zero entrada manual innecessària»). Un canvi posterior al Procés no toca les
 * receptes ja fetes. Per-tenant, soft-deletable; `description` is unique per tenant
 * among the active rows (it is both the identifying label and the text shown in the
 * recipe editor's picker).
 */
export const ProcessInput = z
  .object({
    description: nonEmptyString,
    /** Instruccions de base que es copien al pas en crear-lo. */
    instructions: z.string().trim().min(1).max(4000).optional(),
    estimated_duration_minutes: z.number().int().nonnegative().optional(),
    temperature_c: percent.optional(),
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES).default('none'),
  })
  .strict();
export type ProcessInput = z.infer<typeof ProcessInput>;

/**
 * Un recurs que un Procés proposa per defecte: un TIPUS de recurs × una quantitat («1 ×
 * Amassadora»), mai una màquina concreta (quina es tria a l'ordre, OBR-45). Dues
 * amassadores són UNA línia amb quantitat 2. Es copia al pas en crear-lo des del Procés.
 */
export const ProcessResourceInput = z
  .object({
    resource_type_id: uuid,
    quantity: quantity.positive(),
    duration_minutes: z.number().int().nonnegative().optional(),
    sort_order: z.number().int().nonnegative().default(0),
  })
  .strict();
export type ProcessResourceInput = z.infer<typeof ProcessResourceInput>;

export const Process = z
  .object({
    id: uuid,
    tenant_id: uuid,
    description: nonEmptyString,
    instructions: z.string().nullable(),
    estimated_duration_minutes: z.number().int().nonnegative().nullable(),
    temperature_c: percent.nullable(),
    control_type: z.enum(RECIPE_STEP_CONTROL_TYPES),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Process = z.infer<typeof Process>;

// ── resource_types ───────────────────────────────────────────────────────────────

/**
 * A Tipus de recurs (resource_types master, settings · Configuració · Recursos).
 * The tenant's own classification of what an elaboració consumes besides matèria
 * primera — 'Forn', 'Operari', 'Cambra de fred'. Editable per tenant rather than a
 * fixed enum because the useful set differs by sector (a forn needs 'Forn', a
 * xarcuteria 'Cambra de curació'). `name` is unique per tenant among the active rows.
 */
export const ResourceTypeInput = z
  .object({
    name: nonEmptyString,
  })
  .strict();
export type ResourceTypeInput = z.infer<typeof ResourceTypeInput>;

export const ResourceType = z
  .object({
    id: uuid,
    tenant_id: uuid,
    name: nonEmptyString,
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type ResourceType = z.infer<typeof ResourceType>;

// ── resources ────────────────────────────────────────────────────────────────────

/**
 * A Recurs físic (resources master, settings · Configuració · Recursos): one machine
 * of the obrador — 'Amassadora 01', 'Forn 1' — typed by resource_type_id. A recipe
 * asks for a TYPE (recipe_version_resources); the machine is chosen when the work
 * order is planned (work_order_resources, OBR-45).
 *
 * It may say WHERE it is in the obrador's structure (zone / section / location, the
 * finest level wins; all null = the obrador as a whole). The planner's picker puts
 * the machines of the work order's section first.
 *
 * `cost_per_hour` is Onada 2 groundwork: it is stored so the model need not change
 * when costing lands, but NO Onada 1 screen computes or displays a cost from it.
 */
export const ResourceInput = z
  .object({
    name: nonEmptyString,
    resource_type_id: uuid,
    cost_per_hour: money.nonnegative().optional(),
    notes: z.string().trim().min(1).optional(),
    zone_id: uuid.nullable().optional(),
    section_id: uuid.nullable().optional(),
    location_id: uuid.nullable().optional(),
  })
  .strict();
export type ResourceInput = z.infer<typeof ResourceInput>;

export const Resource = z
  .object({
    id: uuid,
    tenant_id: uuid,
    name: nonEmptyString,
    resource_type_id: uuid,
    cost_per_hour: money.nonnegative().nullable(),
    notes: z.string().trim().min(1).nullable(),
    zone_id: uuid.nullable(),
    section_id: uuid.nullable(),
    location_id: uuid.nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Resource = z.infer<typeof Resource>;

// ── legislation_notes ────────────────────────────────────────────────────────────

/**
 * A legal/normative free-text block (legislation_notes master, settings ·
 * Configuració · Legislació i normativa). Per-tenant and global to the obrador: the
 * whole active set is printed, in `sort_order`, as the CLOSING section of every
 * Fitxa Tècnica. Unlike the named masters the identifying field is the `body`, so
 * `title` is optional and not unique — two blocks may share a heading or have none.
 */
export const LegislationNoteInput = z
  .object({
    title: nonEmptyString.max(200).optional(),
    body: nonEmptyString.max(4000),
    sort_order: z.number().int().nonnegative().default(0),
  })
  .strict();
export type LegislationNoteInput = z.infer<typeof LegislationNoteInput>;

export const LegislationNote = z
  .object({
    id: uuid,
    tenant_id: uuid,
    title: nonEmptyString.nullable(),
    body: nonEmptyString,
    sort_order: z.number().int(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type LegislationNote = z.infer<typeof LegislationNote>;

// ── label_templates ─────────────────────────────────────────────────────────────

/**
 * A label format (label_templates master, settings · formats d'etiqueta). Manages
 * name + label_type + the per-type default flag (at most one active default per
 * type — DB partial unique index) AND the LAYOUT (layout_json — see LabelLayout
 * in schemas/label.ts): the label designer writes the block list + dimensions.
 * `layout_json` is optional so a bare create still works (the editor seeds a
 * default layout). Articles may pin a default template
 * (articles.default_label_template_id); the label is still ONLY generated at OT
 * close / packaging, never from the FT (CLAUDE.md invariant).
 */
export const LabelTemplateInput = z
  .object({
    name: nonEmptyString,
    label_type: z.enum(LABEL_TYPES),
    is_default: z.boolean().default(false),
    layout_json: LabelLayout.optional(),
  })
  .strict();
export type LabelTemplateInput = z.infer<typeof LabelTemplateInput>;

// ── devices ──────────────────────────────────────────────────────────────────────

export const DeviceInput = z
  .object({
    device_type: z.enum(DEVICE_TYPES),
    name: nonEmptyString,
    identifier: nonEmptyString.nullable().optional(),
    status: z.enum(DEVICE_STATUSES).default('registered'),
    work_point_id: uuid.nullable().optional(),
    /**
     * Free-form per type (HOOK: scale parameters, label-template binding). For a label
     * printer, what a printer needs is validated by {@link LabelPrinterConfig}; any other
     * key rides along untouched. An EMPTY config is allowed: a printer may be registered
     * before it is given an address (the backoffice flags it as not configured).
     */
    config_json: z.record(z.string(), z.unknown()).optional(),
  })
  .strict()
  .superRefine((device, ctx) => {
    const config = device.config_json;
    if (device.device_type !== 'label_printer' || !config || Object.keys(config).length === 0) {
      return;
    }
    const parsed = LabelPrinterConfig.safeParse(config);
    if (parsed.success) return;
    for (const issue of parsed.error.issues) {
      ctx.addIssue({ ...issue, path: ['config_json', ...issue.path] });
    }
  });
export type DeviceInput = z.infer<typeof DeviceInput>;

export const Device = z
  .object({
    id: uuid,
    tenant_id: uuid,
    device_type: z.enum(DEVICE_TYPES),
    name: z.string(),
    work_point_id: uuid.nullable(),
    identifier: z.string().nullable(),
    status: z.enum(DEVICE_STATUSES),
    config_json: z.record(z.string(), z.unknown()),
    last_seen_at: z.string().nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Device = z.infer<typeof Device>;

// ── article_categories ──────────────────────────────────────────────────────────

/**
 * A tenant-defined article category. Categories are the obrador's own taxonomy for
 * its catalogue (forn, pastisseria, xarcuteria…), editable from the configuration
 * screen; `parent_id` supports an optional one-level subcategory. tenant_id is
 * injected server-side (RLS + DB DEFAULT); `.strict()` rejects a forged one.
 */
export const ArticleCategoryInput = z
  .object({
    name: nonEmptyString,
    parent_id: uuid.nullable().optional(),
    // A category may be pinned to one tipologia (article form nests the
    // category options under the selected type); null = valid for all.
    article_type: z.enum(ARTICLE_TYPES).nullable().optional(),
  })
  .strict();
export type ArticleCategoryInput = z.infer<typeof ArticleCategoryInput>;

// ── clients ───────────────────────────────────────────────────────────────────

/**
 * Structured customer contact (persisted in `clients.contact_json`). Every field is
 * optional — the obrador fills what it has. Kept to a known shape (mirrors
 * SupplierContact) so future sales / dispatch flows can read it without guessing keys.
 */
export const ClientContact = z
  .object({
    contact_name: nonEmptyString.optional(),
    phone: nonEmptyString.optional(),
    email: z.string().trim().email().optional(),
  })
  .strict();
export type ClientContact = z.infer<typeof ClientContact>;

/**
 * Client (customer) create/update payload (settings · clients). The SAME schema
 * validates create and update; `tenant_id` is NEVER accepted from the client (RLS +
 * the DB DEFAULT inject it) and `.strict()` rejects a forged one. `tax_id` / `address`
 * / `notes` are nullable so an edit that clears them persists the clear; `contact_json`
 * defaults to `{}` server-side when omitted.
 */
export const ClientInput = z
  .object({
    /**
     * Opcional a propòsit: el mestre el pot rebre (fitxa manual, importació d'una
     * llista que ja el porta) però mai és obligatori — quan falta, el trigger
     * `assign_client_code` el genera amb la màscara de l'obrador. Enviar-lo buit
     * NO és el mateix que no enviar-lo: `nonEmptyString` rebutja la cadena buida
     * perquè un codi en blanc no arribi a la base de dades disfressat de valor.
     */
    code: nonEmptyString.nullable().optional(),
    name: nonEmptyString,
    commercial_name: nonEmptyString.nullable().optional(),
    tax_id: nonEmptyString.nullable().optional(),
    contact_json: ClientContact.optional(),
    address: nonEmptyString.nullable().optional(),
    notes: nonEmptyString.nullable().optional(),
  })
  .strict();
export type ClientInput = z.infer<typeof ClientInput>;

/**
 * One delivery address of a client (client_shipping_addresses) — the ERP
 * "enviaments / centres / locals" (doc Vendes, Correccions): a NAME that
 * identifies the delivery point ("Konig Barcelona") plus its address, broken into
 * carrer / codi postal / població / província / país, who receives there and how
 * to deliver. At most one row per client is the default (partial unique index);
 * the fiscal address stays on clients.address. Sales orders keep storing the
 * delivery address as a TEXT snapshot (composeShippingAddress puts the parts back
 * together) — these rows only feed the default + the form's dropdown.
 * tenant_id is NEVER accepted (RLS + DB DEFAULT inject it).
 *
 * `code` ("GIR01") is the obrador's own code for the point: optional, unique per
 * client when given, and NEVER generated by Obrava — a made-up code would mean
 * nothing to anyone, while the obrador's own is what its delivery notes carry.
 */
export const ClientShippingAddressInput = z
  .object({
    client_id: uuid,
    code: nonEmptyString.nullable().optional(),
    name: nonEmptyString,
    address: nonEmptyString,
    postal_code: nonEmptyString.nullable().optional(),
    city: nonEmptyString.nullable().optional(),
    province: nonEmptyString.nullable().optional(),
    country: nonEmptyString.nullable().optional(),
    contact_json: ClientContact.optional(),
    delivery_instructions: nonEmptyString.nullable().optional(),
    is_default: z.boolean().default(false),
  })
  .strict();
export type ClientShippingAddressInput = z.infer<typeof ClientShippingAddressInput>;

/**
 * Patch for an existing delivery point. Same fields, all optional and WITHOUT
 * `client_id`: a point never changes owner — moving it would silently deliver one
 * customer's order to another's door.
 */
export const ClientShippingAddressUpdateInput = ClientShippingAddressInput.omit({
  client_id: true,
})
  .partial()
  .strict();
export type ClientShippingAddressUpdateInput = z.infer<
  typeof ClientShippingAddressUpdateInput
>;

// ── user_roles ──────────────────────────────────────────────────────────────────

/** Assign a role to a tenant member. tenant_id/created_by injected server-side. */
export const UserRoleAssignInput = z
  .object({
    user_id: uuid,
    role: z.enum(USER_ROLES),
  })
  .strict();
export type UserRoleAssignInput = z.infer<typeof UserRoleAssignInput>;
